import type { CookieOptions } from './types';

/**
 * Sets a cookie in the browser with full support for standard options.
 *
 * Note: `httpOnly` will be ignored in client-side JavaScript (it's a server-only flag).
 *
 * @param name - The name of the cookie
 * @param value - The value of the cookie (string or boolean)
 * @param options - Optional attributes for controlling cookie behavior
 */
export function setCookie(name: string, value: string | boolean, options?: CookieOptions): void {
  if (typeof document === 'undefined') return;

  const encodedName = encodeURIComponent(name);
  const encodedValue = encodeURIComponent(String(value));
  let cookieStr = `${encodedName}=${encodedValue}`;

  // Handle expiry via days
  if (options?.days !== undefined) {
    const date = new Date();
    date.setTime(date.getTime() + options.days * 864e5);
    cookieStr += `; expires=${date.toUTCString()}`;
  }

  // Or use provided expires
  if (options?.expires) {
    cookieStr += `; expires=${options.expires.toUTCString()}`;
  }

  // Optional max-age (in seconds)
  if (options?.maxAge !== undefined) {
    cookieStr += `; max-age=${options.maxAge}`;
  }

  // Domain
  if (options?.domain) {
    cookieStr += `; domain=${options.domain}`;
  }

  // Path (default to root)
  cookieStr += `; path=${options?.path || '/'}`;

  // Secure
  if (options?.secure) {
    cookieStr += '; secure';
  }

  // SameSite
  if (options?.sameSite) {
    cookieStr += `; samesite=${options.sameSite}`;
  }

  // Note: httpOnly is ignored in browser — can't be set via JS
  // But we can log a warning if someone mistakenly uses it
  if (options?.httpOnly) {
    console.warn('[setCookie] "httpOnly" cannot be set from client-side JavaScript. It is server-only.');
  }

  document.cookie = cookieStr;
}
